import React from "react";
import { Document, Page, Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import { mergeCouponContent } from "../raffleCoupon/couponContentDefaults";

const STUBS_PER_PAGE = 9;
const STUB_COLS = 3;
const STUB_ROWS = 3;
const PAGE_PAD = 10;
const GAP = 6;
const A4_W = 595.28;
const A4_H = 841.89;
const INNER_W = A4_W - PAGE_PAD * 2;
const INNER_H = A4_H - PAGE_PAD * 2;
const CELL_W = (INNER_W - GAP * (STUB_COLS - 1)) / STUB_COLS;
const CELL_H = (INNER_H - GAP * (STUB_ROWS - 1)) / STUB_ROWS;

const ORIG_STUB_W = 294;
const ORIG_STUB_H = 480;
const STUB_SCALE = Math.min(CELL_W / ORIG_STUB_W, CELL_H / ORIG_STUB_H);
const STUB_W = ORIG_STUB_W * STUB_SCALE;
const STUB_H = ORIG_STUB_H * STUB_SCALE;
const pt = (value) => Math.round(value * STUB_SCALE * 10) / 10;

const styles = StyleSheet.create({
  page: {
    padding: PAGE_PAD,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    flexDirection: "row",
    flexWrap: "wrap",
    alignContent: "flex-start",
  },
  cell: {
    width: CELL_W,
    height: CELL_H,
    marginRight: GAP,
    marginBottom: GAP,
    alignItems: "center",
    justifyContent: "center",
  },
  cellEndOfRow: {
    marginRight: 0,
  },
  stub: {
    width: STUB_W,
    height: STUB_H,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#cccccc",
    borderRightWidth: 2,
    borderRightStyle: "dashed",
    borderRightColor: "#888888",
    paddingTop: pt(18),
    paddingBottom: pt(14),
    paddingHorizontal: pt(16),
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: pt(26),
  },
  logo: {
    width: pt(75),
    objectFit: "contain",
  },
  slBadge: {
    borderWidth: 2,
    borderColor: "#222222",
    borderRadius: pt(10),
    paddingVertical: pt(5),
    paddingHorizontal: pt(10),
    maxWidth: STUB_W * 0.55,
  },
  slBadgeText: {
    fontSize: pt(12),
    fontFamily: "Helvetica-Bold",
    color: "#222222",
  },
  fieldGroup: {
    marginBottom: pt(20),
  },
  fieldLabel: {
    fontSize: pt(14),
    fontFamily: "Helvetica-Bold",
    color: "#111111",
    marginBottom: pt(5),
  },
  fieldLine: {
    borderBottomWidth: 2,
    borderBottomColor: "#333333",
    minHeight: pt(28),
    paddingBottom: pt(2),
    justifyContent: "flex-end",
  },
  fieldValue: {
    fontSize: pt(14),
    color: "#111111",
  },
  footer: {
    marginTop: pt(20),
    fontSize: pt(10),
    color: "#333333",
    textAlign: "center",
    lineHeight: 1.35,
  },
});

const Field = ({ label, value, wrap = false }) => (
  <View style={styles.fieldGroup}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <View style={styles.fieldLine}>
      <Text style={styles.fieldValue} wrap={wrap}>{value || " "}</Text>
    </View>
  </View>
);

const LeftStub = ({ stub = {}, logoSrc = "", couponContent }) => {
  const {
    order_no = "N/A",
    name = "",
    cpr = "N/A",
    mob = "",
    raffleCoupon = "",
    drawDateFormatted = "N/A",
  } = stub;

  return (
    <View style={styles.stub}>
      <View style={styles.header}>
        {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : <View style={{ width: pt(75) }} />}
        <View style={styles.slBadge}>
          <Text style={styles.slBadgeText}>SL.NO : {order_no}</Text>
        </View>
      </View>

      <View>
        <Field label="Name :" value={name} />
        <Field label="C.P.R :" value={cpr} />
        <Field label="Mob :" value={mob} />
        <Field label="Raffle Coupon :" value={raffleCoupon} />
        <Field label="Draw Date :" value={drawDateFormatted} wrap />
      </View>

      <Text style={styles.footer} wrap>
        {couponContent.promo_text}
      </Text>
    </View>
  );
};

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const OrderLeftStubPdf = ({ stubs = [], logoSrc = "", content = {} }) => {
  const couponContent = mergeCouponContent(content);
  const pages = chunkArray(stubs, STUBS_PER_PAGE);

  if (!pages.length) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>No stubs to display.</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {pages.map((pageStubs, pageIndex) => (
        <Page key={`page-${pageIndex}`} size="A4" style={styles.page}>
          {pageStubs.map((stub, index) => {
            const isEndOfRow = (index + 1) % STUB_COLS === 0;
            const isLastRow = index >= pageStubs.length - STUB_COLS;
            return (
              <View
                key={`${stub.order_no}-${stub.raffleCoupon}-${index}`}
                style={[
                  styles.cell,
                  isEndOfRow ? styles.cellEndOfRow : null,
                  isLastRow ? { marginBottom: 0 } : null,
                ]}
              >
                <LeftStub stub={stub} logoSrc={logoSrc} couponContent={couponContent} />
              </View>
            );
          })}
        </Page>
      ))}
    </Document>
  );
};

export { STUBS_PER_PAGE, OrderLeftStubPdf };
export default OrderLeftStubPdf;
